/**
 * Shared formatting utilities for dates, numbers, currency, tokens
 */

/**
 * Format a date to a localized string
 * @param date - Date to format
 * @param locale - Locale code (default: "en")
 * @returns Formatted date string
 */
export function formatDate(date: Date | string, locale = "en-GB"): string {
  const d = typeof date === "string" ? new Date(date) : date
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(d)
}

/**
 * Format a date to a localized string with time
 * @param date - Date to format
 * @param locale - Locale code (default: "en-GB")
 * @returns Formatted date and time string
 */
export function formatDateTime(date: Date | string, locale = "en-GB"): string {
  const d = typeof date === "string" ? new Date(date) : date
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d)
}

/**
 * Format a number with thousand separators
 * @param value - Number to format
 * @param locale - Locale code (default: "en")
 * @returns Formatted number string
 */
export function formatNumber(value: number, locale = "en"): string {
  return new Intl.NumberFormat(locale).format(value)
}

/**
 * Format a currency value
 * @param value - Amount to format
 * @param currency - Currency code (default: "EUR")
 * @param locale - Locale code (default: "en")
 * @returns Formatted currency string
 */
export function formatCurrency(
  value: number,
  currency = "EUR",
  locale = "en",
): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
  }).format(value)
}

/**
 * Format CO2 amount with tons unit
 * @param value - CO2 amount in tons
 * @param locale - Locale code (default: "en")
 * @returns Formatted CO2 string with unit
 */
export function formatCO2(value: number, locale = "en"): string {
  return `${formatNumber(value, locale)} tons CO2`
}

/**
 * Format area in hectares
 * @param value - Area in hectares
 * @param locale - Locale code (default: "en")
 * @returns Formatted area string with unit
 */
export function formatHectares(value: number, locale = "en"): string {
  return `${formatNumber(value, locale)} ha`
}

/**
 * Format token serial number for display
 * @param serialNumber - Token serial number
 * @returns Formatted serial number
 */
export function formatTokenSerial(serialNumber: string): string {
  return serialNumber.toUpperCase()
}

/**
 * Format percentage
 * @param value - Value as decimal (e.g., 0.25 for 25%)
 * @param locale - Locale code (default: "en")
 * @param decimals - Number of decimal places (default: 0)
 * @returns Formatted percentage string
 */
export function formatPercentage(
  value: number,
  locale = "en",
  decimals = 0,
): string {
  return new Intl.NumberFormat(locale, {
    style: "percent",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value)
}

/**
 * Truncate text with ellipsis
 * @param text - Text to truncate
 * @param maxLength - Maximum length before truncation
 * @returns Truncated text with ellipsis if needed
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength) + "..."
}

/**
 * Format a file size in bytes to human-readable format
 * @param bytes - Size in bytes
 * @returns Formatted file size string
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B"
  const k = 1024
  const sizes = ["B", "KB", "MB", "GB", "TB"]
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
}

/**
 * Compute the next Carbonomex session open time (09:00 Europe/Oslo).
 * Uses native Intl API — no external timezone library required.
 * Works correctly across CET (UTC+1) and CEST (UTC+2) transitions.
 *
 * @param from - Reference point (defaults to now)
 * @returns UTC Date representing next 09:00 Oslo
 */
export function nextSessionOpen(from = new Date()): Date {
  const OSLO_TZ = "Europe/Oslo"
  const SESSION_HOUR = 9

  const getOsloHour = (d: Date) =>
    parseInt(
      new Intl.DateTimeFormat("en-GB", { timeZone: OSLO_TZ, hour: "numeric", hour12: false })
        .formatToParts(d)
        .find((p) => p.type === "hour")?.value ?? "0",
      10
    )

  const getOsloParts = (d: Date) => {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: OSLO_TZ,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(d)
    return {
      year: parseInt(parts.find((p) => p.type === "year")?.value ?? "2024", 10),
      month: parseInt(parts.find((p) => p.type === "month")?.value ?? "1", 10),
      day: parseInt(parts.find((p) => p.type === "day")?.value ?? "1", 10),
    }
  }

  const { year, month, day } = getOsloParts(from)
  const osloHour = getOsloHour(from)
  const targetDay = osloHour < SESSION_HOUR ? day : day + 1

  // Create a naive UTC date at 09:00 on the target calendar day, then correct for Oslo offset
  const naive = new Date(Date.UTC(year, month - 1, targetDay, SESSION_HOUR, 0, 0))
  const naiveOsloHour = getOsloHour(naive)
  const offsetHours = naiveOsloHour - SESSION_HOUR

  return new Date(naive.getTime() - offsetHours * 3_600_000)
}

const SESSION_OPEN_HOUR = 9
const SESSION_CLOSE_HOUR = 17
const OSLO_TZ = "Europe/Oslo"

function getOsloHourOf(d: Date): number {
  return parseInt(
    new Intl.DateTimeFormat("en-GB", { timeZone: OSLO_TZ, hour: "numeric", hour12: false })
      .formatToParts(d)
      .find((p) => p.type === "hour")?.value ?? "0",
    10
  )
}

/** True if the current Oslo time is within the trading session (09:00–17:00).
 *  Set NEXT_PUBLIC_FORCE_MARKET_STATE=open|closed in .env.local to override for dev testing. */
export function isMarketOpen(from = new Date()): boolean {
  const force = process.env.NEXT_PUBLIC_FORCE_MARKET_STATE
  if (force === "open") return true
  if (force === "closed") return false
  const hour = getOsloHourOf(from)
  return hour >= SESSION_OPEN_HOUR && hour < SESSION_CLOSE_HOUR
}

/** Returns the next session close (17:00 Oslo) after `from`. */
export function nextSessionClose(from = new Date()): Date {
  const hour = getOsloHourOf(from)
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: OSLO_TZ, year: "numeric", month: "2-digit", day: "2-digit",
  })
  const parts = fmt.formatToParts(from)
  const year = parseInt(parts.find((p) => p.type === "year")?.value ?? "2024", 10)
  const month = parseInt(parts.find((p) => p.type === "month")?.value ?? "1", 10)
  const day = parseInt(parts.find((p) => p.type === "day")?.value ?? "1", 10)
  const targetDay = hour < SESSION_CLOSE_HOUR ? day : day + 1
  const naive = new Date(Date.UTC(year, month - 1, targetDay, SESSION_CLOSE_HOUR, 0, 0))
  const naiveHour = getOsloHourOf(naive)
  return new Date(naive.getTime() - (naiveHour - SESSION_CLOSE_HOUR) * 3_600_000)
}
